import {
  Component,
  inject,
  ChangeDetectionStrategy,
  signal,
  computed,
  DestroyRef,
  Output,
  EventEmitter,
  Input,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  TestDataStorageService,
  TestIdType,
  TestDataBatch
} from '@umss/shared/utils';
import { UmssDialogComponent } from '../dialog/dialog.component';
import { UmssDialogHeaderComponent } from '../dialog-header/dialog-header.component';
import { UmssDialogFooterComponent } from '../dialog-footer/dialog-footer.component';

@Component({
  selector: 'umss-test-data-selector-dialog',
  standalone: true,
  imports: [
    CommonModule,
    UmssDialogComponent,
    UmssDialogHeaderComponent,
    UmssDialogFooterComponent
  ],
  templateUrl: './test-data-selector-dialog.component.html',
  styleUrl: './test-data-selector-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TestDataSelectorDialogComponent implements OnChanges {
  private readonly testDataStorage = inject(TestDataStorageService);
  private readonly destroyRef = inject(DestroyRef);

  @Output() closed = new EventEmitter<string | null>();

  @Input() open = false;
  @Input() idType: TestIdType = 'shift-need';

  batches = signal<TestDataBatch[]>([]);
  selectedBatchIndex = signal<number | null>(null);
  isLoading = signal(false);
  errorMessage = signal<string | null>(null);

  selectedBatch = computed(() => {
    const index = this.selectedBatchIndex();
    if (index === null) return null;
    return this.batches()[index] || null;
  });

  selectedIds = computed(() => {
    const batch = this.selectedBatch();
    return batch?.ids || [];
  });

  selectedBatchInfo = computed(() => {
    const batch = this.selectedBatch();
    if (!batch) return '';
    return `${batch.count} ID(s) - ${batch.formattedTime}`;
  });

  isValid = computed(
    () => this.selectedBatchIndex() !== null && this.selectedIds().length > 0
  );

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']?.currentValue === true) {
      this.loadBatches(this.idType);
    }
  }
  loadBatches(idType: TestIdType): void {
    this.isLoading.set(true);
    this.errorMessage.set(null);
    this.selectedBatchIndex.set(null);

    try {
      const batches = this.testDataStorage.getBatches(idType);
      this.batches.set(batches);
      if (batches.length === 0) {
        this.errorMessage.set(`No saved batch for "${idType}"`);
      } else if (batches.length === 1) {
        this.selectedBatchIndex.set(0);
      }
    } catch (error) {
      this.errorMessage.set('Error loading data from storage');
      console.error('Error loading test data:', error);
    } finally {
      this.isLoading.set(false);
    }
  }

  selectBatch(index: number): void {
    this.selectedBatchIndex.set(
      this.selectedBatchIndex() === index ? null : index
    );
  }

  onConfirm(): void {
    if (!this.isValid()) return;
    this.closed.emit(this.selectedIds().join(','));
  }

  onCancel(): void {
    this.closed.emit(null);
  }

  formatDate(timestamp: number): string {
    return new Date(timestamp).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }
}
